import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Save,
  Loader2,
  Package,
  MessageSquare,
  Eye,
  EyeOff,
  Star,
  Users,
  FolderOpen,
  HelpCircle,
  ShoppingCart,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText,
  LogOut,
  Shield
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [orderDialog, setOrderDialog] = useState({ open: false, order: null });
  const [orderUpdate, setOrderUpdate] = useState({ status: "", admin_notes: "" });

  // Check if user is admin
  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated) {
        toast.error("Admin paneline erişmek için giriş yapmalısınız");
        navigate("/giris");
      } else if (!user?.is_admin) {
        toast.error("Bu sayfaya erişim yetkiniz yok");
        navigate("/dashboard");
      }
    }
  }, [user, isAuthenticated, authLoading, navigate]);

  const handleLogout = () => {
    logout();
    toast.success("Çıkış yapıldı");
    navigate("/");
  };

  const categories = [
    { value: "mobile_app", label: "Mobil Uygulama" },
    { value: "web_app", label: "Web Uygulama" },
    { value: "automation", label: "Otomasyon" },
    { value: "ai_solution", label: "Yapay Zeka" }
  ];

  useEffect(() => {
    if (user?.is_admin) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, productsRes, testimonialsRes, portfolioRes, faqsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/products`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/portfolio`),
        axios.get(`${API}/faqs`),
        axios.get(`${API}/contact`)
      ]);
      setOrders(ordersRes.data);
      setProducts(productsRes.data);
      setTestimonials(testimonialsRes.data);
      setPortfolio(portfolioRes.data);
      setFaqs(faqsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Veriler yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (type, item = null) => {
    setDialogType(type);
    setEditingItem(item);
    
    if (type === "product") {
      setFormData(item ? {
        name: item.name,
        category: item.category,
        description: item.description,
        features: item.features?.join("\n") || "",
        price: item.price,
        price_note: item.price_note || "",
        is_popular: item.is_popular,
        is_active: item.is_active
      } : {
        name: "", category: "mobile_app", description: "", features: "",
        price: "", price_note: "", is_popular: false, is_active: true
      });
    } else if (type === "testimonial") {
      setFormData(item ? {
        name: item.name,
        company: item.company,
        role: item.role,
        content: item.content,
        rating: item.rating,
        is_active: item.is_active
      } : {
        name: "", company: "", role: "", content: "", rating: 5, is_active: true
      });
    } else if (type === "portfolio") {
      setFormData(item ? {
        title: item.title,
        category: item.category,
        description: item.description,
        image_url: item.image_url || "",
        technologies: item.technologies?.join("\n") || "",
        client_name: item.client_name || "",
        project_url: item.project_url || "",
        is_featured: item.is_featured,
        is_active: item.is_active
      } : {
        title: "", category: "mobile_app", description: "", image_url: "",
        technologies: "", client_name: "", project_url: "", is_featured: false, is_active: true
      });
    } else if (type === "faq") {
      setFormData(item ? {
        question: item.question,
        answer: item.answer,
        is_active: item.is_active
      } : {
        question: "", answer: "", is_active: true
      });
    }
    
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      let endpoint, payload;
      
      if (dialogType === "product") {
        endpoint = "products";
        payload = { ...formData, features: formData.features.split("\n").filter(f => f.trim()) };
      } else if (dialogType === "testimonial") {
        endpoint = "testimonials";
        payload = formData;
      } else if (dialogType === "portfolio") {
        endpoint = "portfolio";
        payload = { ...formData, technologies: formData.technologies.split("\n").filter(t => t.trim()) };
      } else if (dialogType === "faq") {
        endpoint = "faqs";
        payload = formData;
      }

      if (editingItem) {
        await axios.put(`${API}/${endpoint}/${editingItem.id}`, payload);
        toast.success("Güncellendi.");
      } else {
        await axios.post(`${API}/${endpoint}`, payload);
        toast.success("Eklendi.");
      }
      
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Kaydetme başarısız oldu.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm("Silmek istediğinizden emin misiniz?")) return;

    const endpoints = { product: "products", testimonial: "testimonials", portfolio: "portfolio", faq: "faqs" };
    
    try {
      await axios.delete(`${API}/${endpoints[type]}/${id}`);
      toast.success("Silindi.");
      fetchData();
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Silme başarısız oldu.");
    }
  };

  const toggleActive = async (type, item) => {
    const endpoints = { product: "products", testimonial: "testimonials", portfolio: "portfolio", faq: "faqs" };
    
    try {
      await axios.put(`${API}/${endpoints[type]}/${item.id}`, { is_active: !item.is_active });
      toast.success(item.is_active ? "Gizlendi." : "Yayınlandı.");
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız.");
    }
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  const getProjectTypeLabel = (type) => {
    const labels = { mobile_app: "Mobil Uygulama", web_development: "Web Geliştirme", n8n_project: "n8n Projesi", ai_systems: "Yapay Zeka", other: "Diğer", web_app: "Web Uygulama", automation: "Otomasyon", ai_solution: "Yapay Zeka" };
    return labels[type] || type;
  };

  const getStatusInfo = (status) => {
    const statuses = {
      pending: { label: "Beklemede", color: "#f97316", icon: Clock },
      confirmed: { label: "Onaylandı", color: "#0ea5e9", icon: CheckCircle2 },
      in_progress: { label: "Devam Ediyor", color: "#a855f7", icon: Package },
      review: { label: "İncelemede", color: "#fbbf24", icon: FileText },
      completed: { label: "Tamamlandı", color: "#00d4aa", icon: CheckCircle2 },
      cancelled: { label: "İptal Edildi", color: "#ef4444", icon: AlertCircle }
    };
    return statuses[status] || statuses.pending;
  };

  const orderStatuses = [
    { value: "pending", label: "Beklemede" },
    { value: "confirmed", label: "Onaylandı" },
    { value: "in_progress", label: "Devam Ediyor" },
    { value: "review", label: "İncelemede" },
    { value: "completed", label: "Tamamlandı" },
    { value: "cancelled", label: "İptal Edildi" }
  ];

  const openOrderDialog = (order) => {
    setOrderDialog({ open: true, order });
    setOrderUpdate({ status: order.status, admin_notes: order.admin_notes || "" });
  };

  const handleOrderUpdate = async () => {
    if (!orderDialog.order) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/orders/${orderDialog.order.id}`, orderUpdate);
      toast.success("Sipariş güncellendi");
      setOrderDialog({ open: false, order: null });
      fetchData();
    } catch (error) {
      toast.error("Güncelleme başarısız");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const renderItemRow = (type, item, fields) => (
    <div key={item.id} className={`bg-[#16161f] border rounded-xl p-6 ${item.is_active ? 'border-[#2a2a3a]' : 'border-[#2a2a3a] opacity-60'}`}>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold text-[#f0f0f5]">{fields.title}</h3>
            {fields.badges}
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-[#9ca3af]">
            {fields.meta}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => toggleActive(type, item)} className={item.is_active ? 'text-[#00d4aa]' : 'text-[#9ca3af]'}>
            {item.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => openDialog(type, item)} className="text-[#0ea5e9]">
            <Pencil className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(type, item.id)} className="text-red-500">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#0a0a0f]" data-testid="admin-page">
      {authLoading ? (
        <div className="flex items-center justify-center py-40">
          <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin" />
        </div>
      ) : !user?.is_admin ? (
        <div className="flex items-center justify-center py-40">
          <div className="text-center">
            <Shield className="w-16 h-16 text-[#9ca3af] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#f0f0f5] mb-2">Erişim Engellendi</h2>
            <p className="text-[#9ca3af]">Bu sayfaya erişim yetkiniz yok.</p>
          </div>
        </div>
      ) : (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-3xl font-bold text-[#f0f0f5]">Admin Panel</h1>
              <span className="px-2 py-1 text-xs font-medium bg-[#00d4aa]/20 text-[#00d4aa] rounded-full flex items-center gap-1">
                <Shield className="w-3 h-3" />Admin
              </span>
            </div>
            <p className="text-[#9ca3af]">Hoş geldin, {user?.name}</p>
          </div>
          <Button variant="outline" onClick={handleLogout} className="border-[#2a2a3a] text-[#9ca3af]">
            <LogOut className="w-4 h-4 mr-2" />Çıkış
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#16161f] border border-[#2a2a3a] mb-6 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#00d4aa] data-[state=active]:text-[#0a0a0f]">
              <ShoppingCart className="w-4 h-4 mr-2" />Siparişler ({orders.length})
            </TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-[#00d4aa] data-[state=active]:text-[#0a0a0f]">
              <Package className="w-4 h-4 mr-2" />Ürünler ({products.length})
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="data-[state=active]:bg-[#00d4aa] data-[state=active]:text-[#0a0a0f]">
              <Users className="w-4 h-4 mr-2" />Yorumlar ({testimonials.length})
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="data-[state=active]:bg-[#00d4aa] data-[state=active]:text-[#0a0a0f]">
              <FolderOpen className="w-4 h-4 mr-2" />Portföy ({portfolio.length})
            </TabsTrigger>
            <TabsTrigger value="faqs" className="data-[state=active]:bg-[#00d4aa] data-[state=active]:text-[#0a0a0f]">
              <HelpCircle className="w-4 h-4 mr-2" />SSS ({faqs.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="data-[state=active]:bg-[#00d4aa] data-[state=active]:text-[#0a0a0f]">
              <MessageSquare className="w-4 h-4 mr-2" />Talepler ({contacts.length})
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders">
            {loading ? <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin mx-auto" /> : (
              <div className="space-y-4">
                {orders.map(order => {
                  const status = getStatusInfo(order.status);
                  const StatusIcon = status.icon;
                  return (
                    <div key={order.id} className="bg-[#16161f] border border-[#2a2a3a] rounded-xl p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-[#f0f0f5]">{order.product_name}</h3>
                            <span className="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1" style={{ backgroundColor: `${status.color}20`, color: status.color }}>
                              <StatusIcon className="w-3 h-3" />{status.label}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-[#9ca3af]">
                            <span className="px-2 py-1 bg-[#0a0a0f] rounded">{getProjectTypeLabel(order.project_type)}</span>
                            <span className="text-[#00d4aa] font-semibold">{order.price}</span>
                            <span>{formatDate(order.created_at)}</span>
                          </div>
                          {order.description && <p className="text-[#9ca3af] text-sm mt-2 line-clamp-2">{order.description}</p>}
                        </div>
                        <Button onClick={() => openOrderDialog(order)} className="bg-[#0ea5e9] hover:bg-[#0284c7] text-white">
                          <Pencil className="w-4 h-4 mr-2" />Düzenle
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {orders.length === 0 && <p className="text-center text-[#9ca3af] py-10">Henüz sipariş yok</p>}
              </div>
            )}
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="flex justify-end mb-6">
              <Button onClick={() => openDialog("product")} className="bg-[#00d4aa] hover:bg-[#00b894] text-[#0a0a0f]">
                <Plus className="w-4 h-4 mr-2" />Yeni Ürün
              </Button>
            </div>
            {loading ? <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin mx-auto" /> : (
              <div className="space-y-4">
                {products.map(item => renderItemRow("product", item, {
                  title: item.name,
                  badges: item.is_popular && <span className="px-2 py-1 text-xs bg-[#00d4aa]/20 text-[#00d4aa] rounded-full flex items-center gap-1"><Star className="w-3 h-3" fill="currentColor" />Popüler</span>,
                  meta: <><span className="px-2 py-1 bg-[#0a0a0f] rounded">{getCategoryLabel(item.category)}</span><span className="text-[#00d4aa] font-semibold">{item.price}</span></>
                }))}
                {products.length === 0 && <p className="text-center text-[#9ca3af] py-10">Henüz ürün yok</p>}
              </div>
            )}
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <div className="flex justify-end mb-6">
              <Button onClick={() => openDialog("testimonial")} className="bg-[#00d4aa] hover:bg-[#00b894] text-[#0a0a0f]">
                <Plus className="w-4 h-4 mr-2" />Yeni Yorum
              </Button>
            </div>
            {loading ? <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin mx-auto" /> : (
              <div className="space-y-4">
                {testimonials.map(item => renderItemRow("testimonial", item, {
                  title: item.name,
                  badges: <span className="text-[#fbbf24]">{"★".repeat(item.rating)}</span>,
                  meta: <><span>{item.role}, {item.company}</span></>
                }))}
                {testimonials.length === 0 && <p className="text-center text-[#9ca3af] py-10">Henüz yorum yok</p>}
              </div>
            )}
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio">
            <div className="flex justify-end mb-6">
              <Button onClick={() => openDialog("portfolio")} className="bg-[#00d4aa] hover:bg-[#00b894] text-[#0a0a0f]">
                <Plus className="w-4 h-4 mr-2" />Yeni Proje
              </Button>
            </div>
            {loading ? <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin mx-auto" /> : (
              <div className="space-y-4">
                {portfolio.map(item => renderItemRow("portfolio", item, {
                  title: item.title,
                  badges: item.is_featured && <span className="px-2 py-1 text-xs bg-[#00d4aa]/20 text-[#00d4aa] rounded-full">Öne Çıkan</span>,
                  meta: <><span className="px-2 py-1 bg-[#0a0a0f] rounded">{getCategoryLabel(item.category)}</span>{item.client_name && <span>{item.client_name}</span>}</>
                }))}
                {portfolio.length === 0 && <p className="text-center text-[#9ca3af] py-10">Henüz proje yok</p>}
              </div>
            )}
          </TabsContent>

          {/* FAQs Tab */}
          <TabsContent value="faqs">
            <div className="flex justify-end mb-6">
              <Button onClick={() => openDialog("faq")} className="bg-[#00d4aa] hover:bg-[#00b894] text-[#0a0a0f]">
                <Plus className="w-4 h-4 mr-2" />Yeni SSS
              </Button>
            </div>
            {loading ? <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin mx-auto" /> : (
              <div className="space-y-4">
                {faqs.map(item => renderItemRow("faq", item, {
                  title: item.question,
                  badges: null,
                  meta: <span className="line-clamp-1">{item.answer}</span>
                }))}
                {faqs.length === 0 && <p className="text-center text-[#9ca3af] py-10">Henüz SSS yok</p>}
              </div>
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            {loading ? <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin mx-auto" /> : (
              <div className="space-y-4">
                {contacts.map(contact => (
                  <div key={contact.id} className="bg-[#16161f] border border-[#2a2a3a] rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-[#f0f0f5]">{contact.name}</h3>
                      <span className="px-2 py-1 text-xs bg-[#00d4aa]/20 text-[#00d4aa] rounded-full">{getProjectTypeLabel(contact.project_type)}</span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2 text-sm text-[#9ca3af] mb-4">
                      <div><span className="text-[#f0f0f5]">Email:</span> {contact.email}</div>
                      <div><span className="text-[#f0f0f5]">Telefon:</span> {contact.phone}</div>
                      <div><span className="text-[#f0f0f5]">Tarih:</span> {formatDate(contact.created_at)}</div>
                    </div>
                    <div className="bg-[#0a0a0f] rounded-lg p-4">
                      <p className="text-[#9ca3af] text-sm whitespace-pre-wrap">{contact.project_details}</p>
                    </div>
                  </div>
                ))}
                {contacts.length === 0 && <p className="text-center text-[#9ca3af] py-10">Henüz talep yok</p>}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Universal Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="bg-[#16161f] border-[#2a2a3a] text-[#f0f0f5] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? "Düzenle" : "Yeni Ekle"}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              {dialogType === "product" && (
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Ürün Adı *</Label>
                      <Input value={formData.name || ""} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a]" />
                    </div>
                    <div className="space-y-2">
                      <Label>Kategori</Label>
                      <Select value={formData.category} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                        <SelectTrigger className="bg-[#0a0a0f] border-[#2a2a3a]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#16161f] border-[#2a2a3a]">
                          {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Açıklama *</Label>
                    <Textarea value={formData.description || ""} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a]" rows={3} />
                  </div>
                  <div className="space-y-2">
                    <Label>Özellikler (her satıra bir)</Label>
                    <Textarea value={formData.features || ""} onChange={(e) => setFormData(p => ({ ...p, features: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a] font-mono" rows={4} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Fiyat *</Label>
                      <Input value={formData.price || ""} onChange={(e) => setFormData(p => ({ ...p, price: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a]" placeholder="₺25.000" />
                    </div>
                    <div className="space-y-2">
                      <Label>Fiyat Notu</Label>
                      <Input value={formData.price_note || ""} onChange={(e) => setFormData(p => ({ ...p, price_note: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a]" />
                    </div>
                  </div>
                  <div className="flex gap-6">
                    <div className="flex items-center gap-3"><Switch checked={formData.is_popular || false} onCheckedChange={(c) => setFormData(p => ({ ...p, is_popular: c }))} /><Label>Popüler</Label></div>
                    <div className="flex items-center gap-3"><Switch checked={formData.is_active !== false} onCheckedChange={(c) => setFormData(p => ({ ...p, is_active: c }))} /><Label>Yayında</Label></div>
                  </div>
                </>
              )}

              {dialogType === "testimonial" && (
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>İsim *</Label><Input value={formData.name || ""} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a]" /></div>
                    <div className="space-y-2"><Label>Şirket *</Label><Input value={formData.company || ""} onChange={(e) => setFormData(p => ({ ...p, company: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a]" /></div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Pozisyon *</Label><Input value={formData.role || ""} onChange={(e) => setFormData(p => ({ ...p, role: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a]" /></div>
                    <div className="space-y-2">
                      <Label>Puan</Label>
                      <Select value={String(formData.rating || 5)} onValueChange={(v) => setFormData(p => ({ ...p, rating: parseInt(v) }))}>
                        <SelectTrigger className="bg-[#0a0a0f] border-[#2a2a3a]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#16161f] border-[#2a2a3a]">
                          {[5,4,3,2,1].map(r => <SelectItem key={r} value={String(r)}>{r} Yıldız</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2"><Label>Yorum *</Label><Textarea value={formData.content || ""} onChange={(e) => setFormData(p => ({ ...p, content: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a]" rows={4} /></div>
                  <div className="flex items-center gap-3"><Switch checked={formData.is_active !== false} onCheckedChange={(c) => setFormData(p => ({ ...p, is_active: c }))} /><Label>Yayında</Label></div>
                </>
              )}

              {dialogType === "portfolio" && (
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Proje Adı *</Label><Input value={formData.title || ""} onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a]" /></div>
                    <div className="space-y-2">
                      <Label>Kategori</Label>
                      <Select value={formData.category} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                        <SelectTrigger className="bg-[#0a0a0f] border-[#2a2a3a]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#16161f] border-[#2a2a3a]">
                          {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2"><Label>Açıklama *</Label><Textarea value={formData.description || ""} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a]" rows={3} /></div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Müşteri Adı</Label><Input value={formData.client_name || ""} onChange={(e) => setFormData(p => ({ ...p, client_name: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a]" /></div>
                    <div className="space-y-2"><Label>Proje URL</Label><Input value={formData.project_url || ""} onChange={(e) => setFormData(p => ({ ...p, project_url: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a]" /></div>
                  </div>
                  <div className="space-y-2"><Label>Görsel URL</Label><Input value={formData.image_url || ""} onChange={(e) => setFormData(p => ({ ...p, image_url: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a]" /></div>
                  <div className="space-y-2"><Label>Teknolojiler (her satıra bir)</Label><Textarea value={formData.technologies || ""} onChange={(e) => setFormData(p => ({ ...p, technologies: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a] font-mono" rows={3} /></div>
                  <div className="flex gap-6">
                    <div className="flex items-center gap-3"><Switch checked={formData.is_featured || false} onCheckedChange={(c) => setFormData(p => ({ ...p, is_featured: c }))} /><Label>Öne Çıkan</Label></div>
                    <div className="flex items-center gap-3"><Switch checked={formData.is_active !== false} onCheckedChange={(c) => setFormData(p => ({ ...p, is_active: c }))} /><Label>Yayında</Label></div>
                  </div>
                </>
              )}

              {dialogType === "faq" && (
                <>
                  <div className="space-y-2"><Label>Soru *</Label><Input value={formData.question || ""} onChange={(e) => setFormData(p => ({ ...p, question: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a]" /></div>
                  <div className="space-y-2"><Label>Cevap *</Label><Textarea value={formData.answer || ""} onChange={(e) => setFormData(p => ({ ...p, answer: e.target.value }))} className="bg-[#0a0a0f] border-[#2a2a3a]" rows={4} /></div>
                  <div className="flex items-center gap-3"><Switch checked={formData.is_active !== false} onCheckedChange={(c) => setFormData(p => ({ ...p, is_active: c }))} /><Label>Yayında</Label></div>
                </>
              )}

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)} className="text-[#9ca3af]">İptal</Button>
                <Button type="submit" disabled={saving} className="bg-[#00d4aa] hover:bg-[#00b894] text-[#0a0a0f]">
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Kaydediliyor...</> : <><Save className="w-4 h-4 mr-2" />Kaydet</>}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Order Update Dialog */}
        <Dialog open={orderDialog.open} onOpenChange={(open) => setOrderDialog({ open, order: open ? orderDialog.order : null })}>
          <DialogContent className="bg-[#16161f] border-[#2a2a3a] text-[#f0f0f5] max-w-lg">
            <DialogHeader>
              <DialogTitle>Sipariş Güncelle</DialogTitle>
            </DialogHeader>

            {orderDialog.order && (
              <div className="space-y-4">
                <div className="bg-[#0a0a0f] rounded-lg p-4">
                  <h3 className="font-semibold text-[#f0f0f5] mb-1">{orderDialog.order.product_name}</h3>
                  <p className="text-[#9ca3af] text-sm">{orderDialog.order.description}</p>
                  <p className="text-[#00d4aa] font-bold mt-2">{orderDialog.order.price}</p>
                </div>

                <div className="space-y-2">
                  <Label>Durum</Label>
                  <Select value={orderUpdate.status} onValueChange={(v) => setOrderUpdate(p => ({ ...p, status: v }))}>
                    <SelectTrigger className="bg-[#0a0a0f] border-[#2a2a3a]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#16161f] border-[#2a2a3a]">
                      {orderStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Admin Notu</Label>
                  <Textarea
                    value={orderUpdate.admin_notes}
                    onChange={(e) => setOrderUpdate(p => ({ ...p, admin_notes: e.target.value }))}
                    placeholder="Müşteriye gösterilecek not..."
                    rows={3}
                    className="bg-[#0a0a0f] border-[#2a2a3a] resize-none"
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="ghost" onClick={() => setOrderDialog({ open: false, order: null })} className="text-[#9ca3af]">İptal</Button>
              <Button onClick={handleOrderUpdate} disabled={saving} className="bg-[#00d4aa] hover:bg-[#00b894] text-[#0a0a0f]">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Kaydediliyor...</> : <><Save className="w-4 h-4 mr-2" />Güncelle</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      )}
    </div>
  );
};

export default AdminPage;
